import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { captureUtm } from "@/lib/utm";

// Public site (keep as per user: "add if any existing feature doesn't included in the master script")
import Home from "@/pages/Home";
import Projects from "@/pages/Projects";
import ProjectDetail from "@/pages/ProjectDetail";
import BuilderProfile from "@/pages/BuilderProfile";
import EMIPage from "@/pages/EMIPage";
import ForBuilders from "@/pages/ForBuilders";
import PublicLayout from "@/pages/PublicLayout";
import SiteLanding from "@/pages/SiteLanding";
import PublicBuilderReport from "@/pages/PublicBuilderReport";

import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import BuyerProfile from "@/pages/BuyerProfile";

// OFR OS (primary CRM)
import OSLogin from "@/os/OSLogin";
import OSLayout from "@/os/OSLayout";
import OSDashboard from "@/os/OSDashboard";
import OSLeads from "@/os/OSLeads";
import OSPipeline from "@/os/OSPipeline";
import OSConnectors from "@/os/OSConnectors";
import OSLoans from "@/os/OSLoans";
import OSFollowups from "@/os/OSFollowups";
import OSCalls from "@/os/OSCalls";
import OSAnalytics from "@/os/OSAnalytics";
import OSTeam from "@/os/OSTeam";

const OS_ROLES = ["admin", "manager", "agent", "telecaller"];

function OSGate({ children, roles }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="min-h-screen flex items-center justify-center" style={{background:"#0B0C0E", color:"#F2F3F5"}}>Loading…</div>;
  if (!user) return <Navigate to="/os/login" replace />;
  const allow = roles || OS_ROLES;
  if (!allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function ProtectedByRole({ allow, children }) {
  const { user, checking } = useAuth();
  if (checking) return <div className="min-h-screen flex items-center justify-center text-forest">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!allow.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function FragmentAuthGate() {
  const loc = useLocation();
  if (loc.hash && loc.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return null;
}

function App() {
  useEffect(() => { captureUtm(); }, []);
  return (
    <div className="App font-sans">
      <AuthProvider>
        <BrowserRouter>
          <Toaster richColors position="top-right" theme="dark" />
          <FragmentAuthGate />
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Public marketing site */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/builders/:idOrSlug" element={<BuilderProfile />} />
              <Route path="/emi" element={<EMIPage />} />
              <Route path="/for-builders" element={<ForBuilders />} />
              <Route path="/site/:slug" element={<SiteLanding />} />
              <Route path="/builder-report/:token" element={<PublicBuilderReport />} />
              <Route path="/me" element={
                <ProtectedByRole allow={["buyer", "admin"]}><BuyerProfile /></ProtectedByRole>
              } />
            </Route>

            {/* Buyer / general auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* OFR OS — primary CRM */}
            <Route path="/os/login" element={<OSLogin />} />
            <Route path="/os" element={<OSGate><OSLayout /></OSGate>}>
              <Route index element={<OSDashboard />} />
              <Route path="leads" element={<OSLeads />} />
              <Route path="pipeline" element={<OSPipeline />} />
              <Route path="connectors" element={<OSConnectors />} />
              <Route path="loans" element={<OSLoans />} />
              <Route path="followups" element={<OSFollowups />} />
              <Route path="calls" element={<OSCalls />} />
              <Route path="analytics" element={<OSAnalytics />} />
              <Route path="team" element={<OSGate roles={["admin","manager"]}><OSTeam /></OSGate>} />
            </Route>

            {/* Legacy admin routes → redirect into OS */}
            <Route path="/admin/*" element={<Navigate to="/os" replace />} />
            <Route path="/telecaller/*" element={<Navigate to="/os" replace />} />
            <Route path="/builder/*" element={<Navigate to="/os" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
